import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import "@/index.css";
import App from "@/App";

// Swallow benign cross-origin "Script error." events (from embedded TradingView iframe)
// before webpack-dev-server / React error overlay picks them up.
if (typeof window !== "undefined") {
  const isBenign = (msg, src) =>
    msg === "Script error." ||
    msg === "ResizeObserver loop limit exceeded" ||
    msg === "ResizeObserver loop completed with undelivered notifications." ||
    (typeof src === "string" && /tradingview\.com|s3\.tradingview\.com/.test(src));

  window.addEventListener(
    "error",
    (e) => {
      if (isBenign(e.message, e.filename)) {
        e.stopImmediatePropagation();
        e.preventDefault();
        return true;
      }
    },
    true,
  );
  window.addEventListener(
    "unhandledrejection",
    (e) => {
      const reason = e.reason && (e.reason.message || String(e.reason));
      if (isBenign(reason, "")) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    },
    true,
  );

  // Belt & braces: if the CRA error overlay iframe still appears with only
  // "Script error." (no real component stack), auto-dismiss it. Real errors
  // include our filenames (.jsx/.js:line), so those overlays stay visible.
  const dismissBenignOverlay = () => {
    document.querySelectorAll("body > iframe").forEach((f) => {
      try {
        const doc = f.contentDocument;
        if (!doc || !doc.body) return;
        const text = doc.body.innerText || "";
        if (
          text.includes("Script error.") &&
          !/[a-zA-Z0-9_-]+\.(jsx?|tsx?):\d+/.test(text.replace(/bundle\.js:\d+/g, ""))
        ) {
          f.remove();
        }
      } catch (_) {}
    });
  };
  const startObserver = () => {
    try {
      const obs = new MutationObserver(dismissBenignOverlay);
      obs.observe(document.body, { childList: true, subtree: false });
    } catch (_) {}
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", startObserver);
  } else {
    startObserver();
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));

// Robots sans JS (Facebook, LinkedIn, Twitter…) lisent les balises par défaut d'index.html.
// Quand le JS s'exécute (navigateurs, Googlebot), on retire ces valeurs par défaut marquées
// data-rh pour laisser react-helmet-async poser les balises propres à chaque page (pas de doublon).
if (typeof document !== "undefined") {
  document.querySelectorAll('head meta[data-rh="true"]').forEach((el) => el.remove());
}

root.render(
  <React.StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </HelmetProvider>
  </React.StrictMode>,
);
